/**
 * Inspection Intelligence Hooks
 *
 * React Query hooks for fetching inspection data from Supabase including:
 * - Inspector notes (crowdsourced intelligence)
 * - Checklist templates and runs
 * - Inspection types
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  moderateNote,
  getModerationStatus,
  sanitizeContent,
  type ContributorReputation,
} from '@/services/moderationService';
import type {
  InspectorNote,
  InspectorNoteType,
  EvidenceLevel,
  ChecklistTemplate,
  ChecklistRun,
  ChecklistItem,
  ChecklistResponse,
  InspectionType,
  KnownFact,
  EvidenceAsset,
} from '@/types/inspection';

// ============================================================================
// INSPECTION TYPES
// ============================================================================

export function useInspectionTypes() {
  return useQuery({
    queryKey: ['inspection-types'],
    queryFn: async (): Promise<InspectionType[]> => {
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('inspection_types')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return (data || []) as InspectionType[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// ============================================================================
// INSPECTOR NOTES (Crowdsourced Intelligence)
// ============================================================================

interface UseInspectorNotesOptions {
  jurisdictionId?: string;
  countyId?: number;
  inspectionTypeId?: string;
  noteType?: InspectorNoteType;
  trade?: string;
  searchTerm?: string;
  status?: 'approved' | 'pending';
  minConfidence?: number;
  limit?: number;
}

export function useInspectorNotes(options?: UseInspectorNotesOptions) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['inspector-notes', options],
    queryFn: async (): Promise<InspectorNote[]> => {
      if (!supabase) return [];

      // Note: Using simple select since foreign key relationships may not be set up
      // For enriched data with joins, use the inspector_notes_enriched view
      let query = supabase
        .from('inspector_notes')
        .select('*');

      // By default, only show approved notes
      if (options?.status) {
        query = query.eq('status', options.status);
      } else {
        query = query.eq('status', 'approved');
      }

      if (options?.jurisdictionId) {
        query = query.eq('jurisdiction_id', options.jurisdictionId);
      }

      if (options?.inspectionTypeId) {
        query = query.eq('inspection_type_id', options.inspectionTypeId);
      }

      if (options?.noteType) {
        query = query.eq('note_type', options.noteType);
      }

      if (options?.trade) {
        query = query.eq('trade', options.trade);
      }

      if (options?.searchTerm && options.searchTerm.trim()) {
        query = query.ilike('content', `%${options.searchTerm.trim()}%`);
      }

      if (options?.minConfidence) {
        query = query.gte('confidence_score', options.minConfidence);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      // Order by confidence + engagement
      const { data, error } = await query.order('confidence_score', { ascending: false, nullsFirst: false });

      if (error) throw error;

      // Filter by county client-side if specified
      let results = data || [];
      if (options?.countyId && !options?.jurisdictionId) {
        results = results.filter((note: any) => {
          return note.jurisdictions?.county_id === options.countyId;
        });
      }

      // Get user's votes and confirmations if logged in
      let userVotes: Record<string, number> = {};
      let userConfirmations: Set<string> = new Set();

      if (user && results.length > 0) {
        const noteIds = results.map((n: any) => n.id);

        // Fetch votes
        const { data: votes } = await supabase
          .from('note_votes')
          .select('note_id, vote_value')
          .eq('user_id', user.uid)
          .in('note_id', noteIds);

        if (votes) {
          userVotes = votes.reduce((acc: Record<string, number>, v: any) => {
            acc[v.note_id] = v.vote_value;
            return acc;
          }, {});
        }

        // Fetch confirmations ("Same here")
        const { data: confirmations } = await supabase
          .from('note_confirmations')
          .select('note_id')
          .eq('user_id', user.uid)
          .in('note_id', noteIds);

        if (confirmations) {
          userConfirmations = new Set(confirmations.map((c: any) => c.note_id));
        }
      }

      return results.map((note: any) => {
        // Compute contributor label from reputation
        const repScore = note.contributor_reputation?.[0]?.reputation_score;
        let contributorLabel = 'Contractor';
        if (repScore >= 80) contributorLabel = 'Trusted Contributor';
        else if (repScore >= 60) contributorLabel = 'Regular Contributor';

        return {
          ...note,
          same_here_count: note.same_here_count || 0,
          validation_state: note.validation_state || 'pending',
          _jurisdictionName: note.jurisdictions?.ahj_name,
          _countyName: null, // Could fetch from counties if needed
          _inspectorName: note.inspectors?.display_name,
          _inspectionTypeName: note.inspection_types?.name,
          _userVote: userVotes[note.id] || null,
          _userConfirmed: userConfirmations.has(note.id),
          _contributorLabel: contributorLabel,
          _totalEngagement: (note.upvote_count || 0) + (note.downvote_count || 0) + (note.same_here_count || 0),
        };
      }) as InspectorNote[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get notes for a specific jurisdiction (for JobDetail page)
export function useJurisdictionNotes(jurisdictionId: string | null) {
  return useInspectorNotes({
    jurisdictionId: jurisdictionId || undefined,
    status: 'approved',
    limit: 10,
  });
}

// ============================================================================
// "SAME HERE" CONFIRMATION
// ============================================================================

export function useConfirmNote() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async ({ noteId, confirm }: { noteId: string; confirm: boolean }) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      // Use contractorId from profile, fallback to user.uid
      const orgId = profile?.contractorId || user.uid;

      if (confirm) {
        // Add confirmation
        const { error } = await supabase
          .from('note_confirmations')
          .insert({
            note_id: noteId,
            user_id: user.uid,
            org_id: orgId,
          });

        if (error && error.code !== '23505') throw error; // Ignore duplicate
      } else {
        // Remove confirmation
        const { error } = await supabase
          .from('note_confirmations')
          .delete()
          .eq('note_id', noteId)
          .eq('user_id', user.uid);

        if (error) throw error;
      }

      return { noteId, confirmed: confirm };
    },
    // Optimistic update - immediately update UI without refetching
    onMutate: async ({ noteId, confirm }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['inspector-notes'] });

      // Update all matching queries optimistically
      queryClient.setQueriesData(
        { queryKey: ['inspector-notes'] },
        (oldData: InspectorNote[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((note) => {
            if (note.id !== noteId) return note;
            return {
              ...note,
              _userConfirmed: confirm,
              same_here_count: (note.same_here_count || 0) + (confirm ? 1 : -1),
            };
          });
        }
      );
    },
    // Don't refetch on success - optimistic update is enough
    onError: () => {
      // On error, invalidate to refetch correct state
      queryClient.invalidateQueries({ queryKey: ['inspector-notes'] });
    },
  });
}

// Create a new inspector note
interface CreateNoteInput {
  jurisdictionId: string;
  inspectionTypeId?: string;
  inspectorId?: string;
  noteType: InspectorNoteType;
  content: string;
  evidenceLevel: EvidenceLevel;
  trade?: string;
  supportingUrl?: string;
}

export function useCreateInspectorNote() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      // Use contractorId from profile, fallback to user.uid
      const orgId = profile?.contractorId || user.uid;

      // Fetch contributor reputation for auto-moderation decision
      let reputation: ContributorReputation | null = null;
      const { data: repData } = await supabase
        .from('contributor_reputation')
        .select('reputation_score, contribution_count, validation_count, accuracy_rate')
        .eq('user_id', user.uid)
        .single();

      if (repData) {
        reputation = {
          reputationScore: repData.reputation_score || 50,
          contributionCount: repData.contribution_count || 0,
          validationCount: repData.validation_count || 0,
          accuracyRate: repData.accuracy_rate || 0,
        };
      }

      // Run auto-moderation
      const moderationResult = moderateNote(input.content, input.noteType, reputation);
      const status = getModerationStatus(moderationResult);
      const sanitized = moderationResult.piiDetected.length > 0
        ? sanitizeContent(input.content)
        : null;

      // Insert the note with moderation results
      const { data, error } = await supabase
        .from('inspector_notes')
        .insert({
          jurisdiction_id: input.jurisdictionId,
          inspection_type_id: input.inspectionTypeId || null,
          inspector_id: input.inspectorId || null,
          note_type: input.noteType,
          content: input.content,
          sanitized_content: sanitized,
          evidence_level: input.evidenceLevel,
          trade: input.trade || null,
          supporting_url: input.supportingUrl || null,
          status: status,
          validation_state: status === 'approved' ? 'approved' : 'pending',
          quality_score: moderationResult.qualityScore,
          auto_moderated: true,
          moderation_reason: moderationResult.reasons.join('; '),
          created_by_user_id: user.uid,
          created_by_org_id: orgId,
        })
        .select()
        .single();

      if (error) throw error;

      // Log the moderation action
      const action = moderationResult.autoApproved ? 'auto_approve'
        : moderationResult.autoFlagged ? 'auto_flag'
        : 'auto_approve'; // Default to pending which we treat as soft auto-approve for logging

      await supabase
        .from('moderation_log')
        .insert({
          note_id: data.id,
          action: moderationResult.autoFlagged ? 'auto_flag' : (moderationResult.autoApproved ? 'auto_approve' : 'auto_flag'),
          previous_status: null,
          new_status: status,
          reason: moderationResult.reasons.join('; '),
          pii_detected: moderationResult.piiDetected,
          profanity_detected: moderationResult.profanityDetected,
          spam_indicators: moderationResult.spamIndicators,
          quality_score: moderationResult.qualityScore,
          is_automated: true,
        });

      return { ...data, _moderationResult: moderationResult };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspector-notes'] });
    },
  });
}

// Vote on a note
export function useVoteOnNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ noteId, voteValue, previousVote }: { noteId: string; voteValue: 1 | -1 | 0; previousVote?: number | null }) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      if (voteValue === 0) {
        // Remove vote
        const { error } = await supabase
          .from('note_votes')
          .delete()
          .eq('note_id', noteId)
          .eq('user_id', user.uid);

        if (error) throw error;
      } else {
        // Upsert vote - specify conflict columns for proper upsert behavior
        const { error } = await supabase
          .from('note_votes')
          .upsert(
            {
              note_id: noteId,
              user_id: user.uid,
              vote_value: voteValue,
            },
            { onConflict: 'note_id,user_id' }
          );

        if (error) throw error;
      }

      // Note: Vote counts are updated automatically by database trigger
      // (update_note_vote_counts with SECURITY DEFINER)

      return { noteId, voteValue, previousVote };
    },
    // Optimistic update - immediately update UI without refetching
    onMutate: async ({ noteId, voteValue, previousVote }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['inspector-notes'] });

      // Update all matching queries optimistically
      queryClient.setQueriesData(
        { queryKey: ['inspector-notes'] },
        (oldData: InspectorNote[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((note) => {
            if (note.id !== noteId) return note;

            // Calculate new vote counts
            let upvoteChange = 0;
            let downvoteChange = 0;

            // Remove old vote effect
            if (previousVote === 1) upvoteChange -= 1;
            if (previousVote === -1) downvoteChange -= 1;

            // Add new vote effect
            if (voteValue === 1) upvoteChange += 1;
            if (voteValue === -1) downvoteChange += 1;

            return {
              ...note,
              _userVote: voteValue === 0 ? null : voteValue,
              upvote_count: (note.upvote_count || 0) + upvoteChange,
              downvote_count: (note.downvote_count || 0) + downvoteChange,
            };
          });
        }
      );
    },
    // Don't refetch on success - optimistic update is enough
    // The trigger updates DB counts, which will sync on next natural refetch
    onError: () => {
      // On error, invalidate to refetch correct state
      queryClient.invalidateQueries({ queryKey: ['inspector-notes'] });
    },
  });
}

// ============================================================================
// CHECKLIST TEMPLATES
// ============================================================================

interface UseChecklistTemplatesOptions {
  jurisdictionId?: string;
  inspectionTypeId?: string;
  status?: 'published' | 'draft' | 'deprecated';
}

export function useChecklistTemplates(options?: UseChecklistTemplatesOptions) {
  return useQuery({
    queryKey: ['checklist-templates', options],
    queryFn: async (): Promise<ChecklistTemplate[]> => {
      if (!supabase) return [];

      let query = supabase
        .from('checklist_templates')
        .select(`
          *,
          inspection_types(name),
          jurisdictions(ahj_name)
        `);

      // Default to published only
      if (options?.status) {
        query = query.eq('status', options.status);
      } else {
        query = query.eq('status', 'published');
      }

      if (options?.jurisdictionId) {
        // Include jurisdiction-specific and national (null jurisdiction) templates
        query = query.or(`jurisdiction_id.eq.${options.jurisdictionId},jurisdiction_id.is.null`);
      }

      if (options?.inspectionTypeId) {
        query = query.eq('inspection_type_id', options.inspectionTypeId);
      }

      const { data, error } = await query.order('title');

      if (error) throw error;

      // Get item counts for each template
      const templateIds = (data || []).map((t: any) => t.id);
      let itemCounts: Record<string, number> = {};

      if (templateIds.length > 0) {
        const { data: items } = await supabase
          .from('checklist_items')
          .select('template_id')
          .in('template_id', templateIds);

        if (items) {
          itemCounts = items.reduce((acc: Record<string, number>, item: any) => {
            acc[item.template_id] = (acc[item.template_id] || 0) + 1;
            return acc;
          }, {});
        }
      }

      return (data || []).map((template: any) => ({
        ...template,
        _inspectionTypeName: template.inspection_types?.name,
        _jurisdictionName: template.jurisdictions?.ahj_name,
        _itemCount: itemCounts[template.id] || 0,
      })) as ChecklistTemplate[];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Get checklist items for a template
export function useChecklistItems(templateId: string | null) {
  return useQuery({
    queryKey: ['checklist-items', templateId],
    queryFn: async (): Promise<ChecklistItem[]> => {
      if (!supabase || !templateId) return [];

      const { data, error } = await supabase
        .from('checklist_items')
        .select(`
          *,
          evidence_rules(*)
        `)
        .eq('template_id', templateId)
        .order('section')
        .order('sort_order');

      if (error) throw error;

      return (data || []).map((item: any) => ({
        ...item,
        _evidenceRule: item.evidence_rules || null,
      })) as ChecklistItem[];
    },
    enabled: !!templateId,
    staleTime: 1000 * 60 * 30,
  });
}

// ============================================================================
// CHECKLIST RUNS (per job/project)
// ============================================================================

interface UseChecklistRunsOptions {
  projectId?: string;
  orgId?: string;
  status?: string;
}

export function useChecklistRuns(options?: UseChecklistRunsOptions) {
  return useQuery({
    queryKey: ['checklist-runs', options],
    queryFn: async (): Promise<ChecklistRun[]> => {
      if (!supabase) return [];

      let query = supabase
        .from('checklist_runs')
        .select(`
          *,
          checklist_templates(id, title, description)
        `);

      if (options?.projectId) {
        query = query.eq('project_id', options.projectId);
      }

      if (options?.orgId) {
        query = query.eq('org_id', options.orgId);
      }

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((run: any) => ({
        ...run,
        _template: run.checklist_templates || null,
      })) as ChecklistRun[];
    },
    staleTime: 1000 * 60 * 5,
  });
}

// Get a specific checklist run with responses
export function useChecklistRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ['checklist-run', runId],
    queryFn: async () => {
      if (!supabase || !runId) return null;

      // Get the run
      const { data: run, error: runError } = await supabase
        .from('checklist_runs')
        .select(`
          *,
          checklist_templates(*)
        `)
        .eq('id', runId)
        .single();

      if (runError) throw runError;
      if (!run) return null;

      // Get responses
      const { data: responses, error: responsesError } = await supabase
        .from('checklist_responses')
        .select(`
          *,
          checklist_items(*)
        `)
        .eq('run_id', runId)
        .order('created_at');

      if (responsesError) throw responsesError;

      return {
        ...run,
        _template: run.checklist_templates,
        _responses: (responses || []).map((r: any) => ({
          ...r,
          _item: r.checklist_items,
        })),
      };
    },
    enabled: !!runId,
    staleTime: 1000 * 60 * 2,
  });
}

// Create a new checklist run for a job
interface CreateChecklistRunInput {
  projectId: string;
  templateId: string;
  orgId: string;
}

export function useCreateChecklistRun() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateChecklistRunInput) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      // Create the run
      const { data: run, error: runError } = await supabase
        .from('checklist_runs')
        .insert({
          project_id: input.projectId,
          template_id: input.templateId,
          org_id: input.orgId,
          status: 'in_progress',
          created_by: user.uid,
        })
        .select()
        .single();

      if (runError) throw runError;

      // Get template items and create responses
      const { data: items } = await supabase
        .from('checklist_items')
        .select('id, severity, evidence_rule_id')
        .eq('template_id', input.templateId);

      if (items && items.length > 0) {
        const responses = items.map((item: any) => ({
          run_id: run.id,
          item_id: item.id,
          status: 'pending',
          evidence_status: item.evidence_rule_id ? 'pending' : 'satisfied',
          is_blocker: item.severity === 'blocker',
        }));

        await supabase.from('checklist_responses').insert(responses);
      }

      return run;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['checklist-runs'] });
      queryClient.invalidateQueries({ queryKey: ['checklist-runs', { projectId: variables.projectId }] });
    },
  });
}

// Update a checklist response
interface UpdateChecklistResponseInput {
  responseId: string;
  status?: 'pending' | 'pass' | 'fail' | 'na' | 'skip';
  notes?: string;
}

export function useUpdateChecklistResponse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: UpdateChecklistResponseInput) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('checklist_responses')
        .update({
          status: input.status,
          notes: input.notes,
          answered_by: user.uid,
          answered_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.responseId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['checklist-run', data.run_id] });
      queryClient.invalidateQueries({ queryKey: ['checklist-runs'] });
    },
  });
}


// ============================================================================
// KNOWN FACTS (Static insights)
// ============================================================================

interface UseKnownFactsOptions {
  jurisdictionId?: string;
  countyId?: number;
  category?: string;
  trade?: string; // 'electrical', 'plumbing', 'hvac', 'mechanical', 'structural', 'general', 'fire', 'roofing'
}

export function useKnownFacts(options?: UseKnownFactsOptions) {
  return useQuery({
    queryKey: ['known-facts', options],
    queryFn: async (): Promise<KnownFact[]> => {
      if (!supabase) return [];

      // Join with inspection_types for trade and jurisdictions for county filtering
      let query = supabase
        .from('known_facts')
        .select(`
          *,
          inspection_types(trade),
          jurisdictions(county_id)
        `)
        .eq('is_active', true);

      if (options?.jurisdictionId) {
        // Filter by specific jurisdiction (include null for general facts)
        query = query.or(`jurisdiction_id.eq.${options.jurisdictionId},jurisdiction_id.is.null`);
      }

      if (options?.category) {
        query = query.eq('category', options.category);
      }

      const { data, error } = await query.order('title');

      if (error) throw error;

      // Filter results client-side for trade and county
      let results = (data || []) as (KnownFact & {
        inspection_types?: { trade: string } | null;
        jurisdictions?: { county_id: number } | null;
      })[];

      // Filter by county if specified (and no specific jurisdiction selected)
      if (options?.countyId && !options?.jurisdictionId) {
        results = results.filter((fact) => {
          // Include facts with no jurisdiction (general facts) OR facts in this county
          if (!fact.jurisdiction_id) return true;
          return fact.jurisdictions?.county_id === options.countyId;
        });
      }

      // Filter by trade if specified
      if (options?.trade) {
        results = results.filter((fact) => {
          // Include facts with no inspection_type (general facts) OR facts matching the trade
          if (!fact.inspection_type_id) return true;
          return fact.inspection_types?.trade === options.trade;
        });
      }

      // Map to include _trade field and remove the joined objects
      return results.map(({ inspection_types, jurisdictions, ...fact }) => ({
        ...fact,
        _trade: inspection_types?.trade || null,
      })) as KnownFact[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// ============================================================================
// EVIDENCE ASSETS
// ============================================================================

export function useEvidenceAssets(options?: { runId?: string; itemId?: string }) {
  return useQuery({
    queryKey: ['evidence-assets', options],
    queryFn: async (): Promise<EvidenceAsset[]> => {
      if (!supabase) return [];

      let query = supabase
        .from('evidence_assets')
        .select('*');

      if (options?.runId) {
        query = query.eq('run_id', options.runId);
      }

      if (options?.itemId) {
        query = query.eq('item_id', options.itemId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as EvidenceAsset[];
    },
    enabled: !!(options?.runId || options?.itemId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============================================================================
// MODERATION QUEUE (Admin)
// ============================================================================

export function usePendingNotes(options?: { status?: 'pending' | 'flagged'; limit?: number }) {
  return useQuery({
    queryKey: ['pending-notes', options],
    queryFn: async (): Promise<InspectorNote[]> => {
      if (!supabase) return [];

      let query = supabase
        .from('inspector_notes')
        .select(`
          *,
          jurisdictions(ahj_name),
          inspectors(display_name),
          inspection_types(name)
        `);

      if (options?.status === 'flagged') {
        query = query.eq('status', 'flagged');
      } else {
        query = query.eq('status', 'pending');
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query.order('created_at', { ascending: true });

      if (error) throw error;

      return (data || []).map((note: any) => ({
        ...note,
        _jurisdictionName: note.jurisdictions?.ahj_name,
        _inspectorName: note.inspectors?.display_name,
        _inspectionTypeName: note.inspection_types?.name,
      })) as InspectorNote[];
    },
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

// Flag a note
export function useFlagNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      noteId,
      reason,
      details,
    }: {
      noteId: string;
      reason: 'inaccurate' | 'offensive' | 'pii' | 'spam' | 'other';
      details?: string;
    }) => {
      if (!supabase || !user) {
        throw new Error('Not authenticated');
      }

      // Create flag record
      const { error: flagError } = await supabase
        .from('note_flags')
        .insert({
          note_id: noteId,
          user_id: user.uid,
          reason,
          details,
        });

      if (flagError) throw flagError;

      // Update flag count on the note
      const { data: flags } = await supabase
        .from('note_flags')
        .select('id')
        .eq('note_id', noteId);

      const flagCount = flags?.length || 0;

      // Auto-flag if too many flags
      const updateData: Record<string, unknown> = { flag_count: flagCount };
      if (flagCount >= 3) {
        updateData.status = 'flagged';
      }

      await supabase
        .from('inspector_notes')
        .update(updateData)
        .eq('id', noteId);

      return { noteId, flagCount };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspector-notes'] });
      queryClient.invalidateQueries({ queryKey: ['pending-notes'] });
    },
  });
}

// ============================================================================
// READINESS BLOCKERS
// ============================================================================

export interface BlockerDetail {
  id: string;
  section: string;
  item_text: string;
  reason: 'evidence_missing' | 'failed' | 'pending';
  severity: 'blocker' | 'warn';
}

export function useBlockers(runId: string | null) {
  return useQuery({
    queryKey: ['blockers', runId],
    queryFn: async (): Promise<BlockerDetail[]> => {
      if (!supabase || !runId) return [];

      // Get responses with blockers
      const { data: responses, error } = await supabase
        .from('checklist_responses')
        .select(`
          *,
          checklist_items(id, section, item_text, severity, evidence_rule_id)
        `)
        .eq('run_id', runId)
        .eq('is_blocker', true);

      if (error) throw error;

      return (responses || []).map((r: any) => {
        let reason: BlockerDetail['reason'] = 'pending';
        if (r.status === 'fail') {
          reason = 'failed';
        } else if (r.evidence_status === 'missing' || r.evidence_status === 'partial') {
          reason = 'evidence_missing';
        }

        return {
          id: r.id,
          section: r.checklist_items?.section || 'Unknown',
          item_text: r.checklist_items?.item_text || 'Unknown item',
          reason,
          severity: r.checklist_items?.severity || 'blocker',
        };
      });
    },
    enabled: !!runId,
    staleTime: 1000 * 60 * 2,
  });
}

// ============================================================================
// PHASE 3: GENERATION PIPELINE
// ============================================================================

/**
 * Generate checklist items from known facts and validated inspector notes
 * Calls the generate_checklist_items database function
 */
interface GenerateChecklistItemsInput {
  templateId: string;
  jurisdictionId: number;
  inspectionTypeId?: string;
  trade?: string;
  minConfidence?: number;
}

interface GenerateChecklistItemsResult {
  items_added: number;
  from_facts: number;
  from_notes: number;
}

export function useGenerateChecklistItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GenerateChecklistItemsInput): Promise<GenerateChecklistItemsResult> => {
      if (!supabase) {
        throw new Error('Supabase not initialized');
      }

      const { data, error } = await supabase.rpc('generate_checklist_items', {
        p_template_id: input.templateId,
        p_jurisdiction_id: input.jurisdictionId,
        p_inspection_type_id: input.inspectionTypeId || null,
        p_trade: input.trade || null,
        p_min_confidence: input.minConfidence || 0.7,
      });

      if (error) throw error;

      // RPC returns array with single row
      const result = Array.isArray(data) ? data[0] : data;
      return result as GenerateChecklistItemsResult;
    },
    onSuccess: (_, variables) => {
      // Invalidate checklist items for this template
      queryClient.invalidateQueries({ queryKey: ['checklist-items', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
  });
}

/**
 * Contextual tips for a checklist - returns relevant inspector notes
 */
export interface ContextualTip {
  note_id: string;
  content: string;
  note_type: string;
  confidence_score: number;
  same_here_count: number;
  contributor_label: string;
}

interface UseContextualTipsOptions {
  jurisdictionId: number | null;
  inspectionTypeId?: string;
  trade?: string;
  limit?: number;
}

export function useContextualTips(options: UseContextualTipsOptions) {
  return useQuery({
    queryKey: ['contextual-tips', options],
    queryFn: async (): Promise<ContextualTip[]> => {
      if (!supabase || !options.jurisdictionId) return [];

      // Query inspector_notes directly instead of using RPC
      // This is a workaround until the RPC function is properly exposed
      let query = supabase
        .from('inspector_notes')
        .select('id, content, note_type, confidence_score, same_here_count, validation_state')
        .eq('status', 'approved')
        .eq('jurisdiction_id', options.jurisdictionId.toString())
        .gte('confidence_score', 0.6)
        .order('confidence_score', { ascending: false })
        .limit(options.limit || 5);

      // Filter by trade if provided
      if (options.trade) {
        query = query.or(`trade.eq.${options.trade},trade.is.null`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Map to ContextualTip format
      return (data || []).map((note) => ({
        note_id: note.id,
        content: note.content,
        note_type: note.note_type,
        confidence_score: note.confidence_score || 0,
        same_here_count: note.same_here_count || 0,
        contributor_label: 'Contractor', // Default label since we can't join easily
      }));
    },
    enabled: !!options.jurisdictionId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Get checklist items with source information (enriched view)
 */
export interface EnrichedChecklistItem extends ChecklistItem {
  source_type?: 'baseline' | 'known_fact' | 'inspector_note' | 'manual';
  source_id?: string;
  source_confidence?: number;
  source_title?: string;
  source_category?: string;
  note_same_here_count?: number;
  note_upvote_count?: number;
  note_is_validated?: boolean;
}

export function useEnrichedChecklistItems(templateId: string | null) {
  return useQuery({
    queryKey: ['checklist-items-enriched', templateId],
    queryFn: async (): Promise<EnrichedChecklistItem[]> => {
      if (!supabase || !templateId) return [];

      const { data, error } = await supabase
        .from('checklist_items_enriched')
        .select('*')
        .eq('template_id', templateId)
        .order('section')
        .order('sort_order');

      if (error) throw error;

      return (data || []) as EnrichedChecklistItem[];
    },
    enabled: !!templateId,
    staleTime: 1000 * 60 * 10,
  });
}

// ============================================================================
// COMMUNITY TIPS (For Checklists & Compliance)
// ============================================================================

/**
 * Community tip for display in checklists and compliance checks
 * Contains validated, high-confidence community notes
 */
export interface CommunityTip {
  id: string;
  content: string;
  noteType: InspectorNoteType;
  confidenceScore: number;
  sameHereCount: number;
  upvoteCount: number;
  validationState: string;
  trade: string | null;
  evidenceLevel: EvidenceLevel;
  contributorLabel: string;
}

interface UseCommunityTipsOptions {
  jurisdictionId: number | string | null;
  trade?: string;
  noteTypes?: InspectorNoteType[];
  minConfidence?: number;
  limit?: number;
  context?: 'checklist' | 'compliance';
}

/**
 * Fetch validated community notes for display as tips/warnings
 * Filters for high-confidence, approved notes relevant to the context
 */
export function useCommunityTips(options: UseCommunityTipsOptions) {
  return useQuery({
    queryKey: ['community-tips', options],
    queryFn: async (): Promise<CommunityTip[]> => {
      if (!supabase || !options.jurisdictionId) return [];

      const minConfidence = options.minConfidence ?? 0.5;
      const limit = options.limit ?? 5;

      // Build query with filters
      // Note: We need to build the query step by step due to Supabase typing
      const { data, error } = await supabase
        .from('inspector_notes')
        .select('id, content, note_type, confidence_score, same_here_count, upvote_count, validation_state, trade, evidence_level')
        .eq('status', 'approved')
        .eq('jurisdiction_id', options.jurisdictionId.toString())
        .gte('confidence_score', minConfidence)
        .in('validation_state', ['validated', 'approved'])
        .order('confidence_score', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Filter by note types client-side based on context
      let filteredData = data || [];

      if (options.noteTypes && options.noteTypes.length > 0) {
        filteredData = filteredData.filter((note: any) =>
          options.noteTypes!.includes(note.note_type)
        );
      } else if (options.context === 'checklist') {
        // For checklists, show tips, patterns, and preferences
        filteredData = filteredData.filter((note: any) =>
          ['tip', 'pattern', 'preference'].includes(note.note_type)
        );
      } else if (options.context === 'compliance') {
        // For compliance, show fail reasons, patterns, and tips
        filteredData = filteredData.filter((note: any) =>
          ['fail_reason', 'pattern', 'tip'].includes(note.note_type)
        );
      }

      // Filter by trade if provided
      if (options.trade) {
        filteredData = filteredData.filter((note: any) =>
          !note.trade || note.trade === options.trade
        );
      }

      // Map to CommunityTip format
      return filteredData.map((note: any) => {
        // Calculate contributor label based on engagement
        const engagement = (note.upvote_count || 0) + (note.same_here_count || 0);
        let contributorLabel = 'Contractor';
        if (note.validation_state === 'validated') {
          contributorLabel = 'Verified Contributor';
        } else if (engagement >= 5) {
          contributorLabel = 'Trusted Contributor';
        }

        return {
          id: note.id,
          content: note.content,
          noteType: note.note_type as InspectorNoteType,
          confidenceScore: note.confidence_score || 0,
          sameHereCount: note.same_here_count || 0,
          upvoteCount: note.upvote_count || 0,
          validationState: note.validation_state || 'approved',
          trade: note.trade,
          evidenceLevel: note.evidence_level as EvidenceLevel,
          contributorLabel,
        };
      });
    },
    enabled: !!options.jurisdictionId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
